import type { ListItemIconProps, ListItemProps, ListItemTextProps } from '@mui/material';
import { Box, ListItem, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import type { ComponentProps, ElementType, ReactNode, Ref } from 'react';
import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '~/components/Tooltip';
import { useLink } from '~/hooks/link';
import { useSideNav } from '../context';

export interface NavItemIconProps extends ListItemIconProps {
  icon: ReactNode;
  tooltip: string;
}

export const NavItemIcon = ({ icon, tooltip, ...props }: NavItemIconProps) => {
  const theme = useTheme();
  const { navOpen } = useSideNav();
  return (
    <ListItemIcon
      sx={{
        ...(navOpen && {
          marginRight: theme.spacing(1),
        }),
        ...(!navOpen && {
          marginRight: 0,
          justifyContent: 'center',
        }),
        '&.MuiListItemIcon-root': {
          color: 'primary.mutedText',
        },
      }}
      {...props}
    >
      <Tooltip title={navOpen ? '' : tooltip} placement="right">
        <Box sx={{ display: 'flex' }}>{icon}</Box>
      </Tooltip>
    </ListItemIcon>
  );
};

export interface NavItemTextProps extends ListItemTextProps {
  text: string;
}

export const NavItemText = ({ text, ...props }: NavItemTextProps) => {
  const { navOpen } = useSideNav();
  return (
    <ListItemText
      {...props}
      primary={navOpen ? text : ''}
      sx={{
        ...(!navOpen && {
          display: 'none',
        }),
        textAlign: 'left',
        overflow: 'hidden',
        '& .MuiTypography-root': {
          color: 'primary.mutedText',
          fontSize: '1rem',
          fontWeight: 600,
        },
      }}
    />
  );
};

export type NavItemLinkProps<T extends ElementType = 'div'> = ListItemProps<T> &
  ComponentProps<typeof ListItem> & {
    endIcon?: ReactNode;
    icon?: ReactNode;
    text: string;
    href?: string; // href for traditional anchor tags
    to?: string; // to for React Router's Link
  };

export const NavItemLink = forwardRef(function NavItemLink<T extends ElementType>(
  { endIcon, icon, sx, text, to, href, ...props }: NavItemLinkProps<T>,
  ref: Ref<HTMLElement>,
) {
  const theme = useTheme();
  const { navOpen } = useSideNav();
  const link = useLink();

  const isReactRouterLink = Boolean(to);
  // With nowhere to go it is a menu trigger, and an <a> without an href cannot take focus.
  const isButton = !isReactRouterLink && !href;
  const Component = isReactRouterLink ? Link : isButton ? 'button' : 'a';

  return (
    <ListItem
      component={Component}
      {...(isReactRouterLink && to ? { to: link(to) } : href ? { href } : {})}
      {...(isButton && { type: 'button' })}
      {...(isButton && props.disabled && { tabIndex: -1, 'aria-disabled': true })}
      sx={{
        ...(isButton && {
          backgroundColor: 'transparent',
          border: 0,
          font: 'inherit',
          textAlign: 'left',
          width: '100%',
        }),
        ...(!navOpen && {
          justifyContent: 'center',
        }),
        borderRadius: theme.spacing(0.5),
        color: 'primary.mutedText',
        margin: theme.spacing(0.5, 0),
        padding: theme.spacing(1, 2),
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
        '&.Mui-disabled': {
          cursor: 'default',
          opacity: 1,
          '& .MuiTypography-root': {
            color: 'common.white',
          },
        },
        '&.Mui-selected': {
          backgroundColor: 'primary.dark',
          '& .MuiListItemIcon-root, & .MuiTypography-root': {
            color: theme.palette.common.white,
          },
        },
        '&:hover, &.Mui-selected:hover': {
          backgroundColor: 'primary.hover',
          '& .MuiListItemIcon-root, & .MuiTypography-root': {
            color: theme.palette.common.white,
          },
        },
        ...sx,
      }}
      disableGutters
      // Collapsed, the text is hidden and only the icon's tooltip shows, so the link names itself.
      aria-label={navOpen ? undefined : text}
      {...props}
      ref={ref}
    >
      {icon && <NavItemIcon tooltip={text} icon={icon} />}
      <NavItemText text={text} />
      {navOpen && endIcon && <span style={{ display: 'flex' }}>{endIcon}</span>}
    </ListItem>
  );
});
